import cx from 'classnames'
import { useState, useRef, useEffect } from 'react'
import Tooltip from 'react-tooltip-lite'

import { InfoCircleIcon } from '@emico/icons'

import styles from './InfoBox.module.scss'
import Icon from '../../media/Icon'

interface Props {
    children: React.ReactNode
    className?: string
    tooltipClassName?: string
    hasToggleEnabled?: boolean
    hasStopPropagationEnabled?: boolean
    /**
     * On scroll vertical, hide the infobox when threshold is met.
     */
    hideOnScrollThreshold?: number
}

const InfoBox = ({
    children,
    className,
    tooltipClassName,
    hasToggleEnabled,
    hasStopPropagationEnabled,
    hideOnScrollThreshold,
}: Props) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const triggerRef = useRef<HTMLDivElement>(null)
    const lastScrollY = useRef<number>()

    useEffect(() => {
        const handleDocumentClicks = (e: Event) => {
            if (triggerRef.current?.contains(e.target as Node)) {
                if (hasStopPropagationEnabled) {
                    e.stopImmediatePropagation()
                }
                setIsOpen((open) => !open)
                return
            }
            setIsOpen(false)
        }
        if (hasToggleEnabled) {
            document.addEventListener('click', handleDocumentClicks, {
                capture: hasStopPropagationEnabled,
            })
        }
        return () =>
            document.removeEventListener('click', handleDocumentClicks, {
                capture: hasStopPropagationEnabled,
            })
    }, [hasToggleEnabled, hasStopPropagationEnabled])

    useEffect(() => {
        if (
            hideOnScrollThreshold === undefined ||
            hideOnScrollThreshold === null
        ) {
            return
        }

        const handleScroll = () => {
            if (isOpen) {
                if (
                    Math.abs((lastScrollY.current ?? 0) - window.scrollY) >=
                    hideOnScrollThreshold
                ) {
                    setIsOpen(false)
                }
            }
        }

        if (isOpen) {
            lastScrollY.current = window.scrollY
            window.addEventListener('scroll', handleScroll)
        }

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [isOpen, hideOnScrollThreshold])

    return (
        <Tooltip
            className={cx(styles.tooltip, className)}
            tipContentClassName={cx(styles.tooltipContent, tooltipClassName)}
            content={children}
            distance={18}
            useHover={!hasToggleEnabled}
            eventToggle={hasToggleEnabled ? 'onClick' : undefined}
            isOpen={isOpen}
            padding="8px 17px"
        >
            <div className={styles.trigger} ref={triggerRef}>
                <Icon component={InfoCircleIcon} title="" />
            </div>
        </Tooltip>
    )
}

export default InfoBox
